import React, { useRef, useEffect, useState, useMemo } from "react";
import { t } from "../i18n";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import "./VideoPreview.css";

/**
 * VideoPreview - Continuous preview player with slideshow fallback
 *
 * Playback modes (per segment):
 *  1. Video ready   → plays the real <video>
 *  2. Images exist  → crossfade slideshow (start → end image)
 *  3. Nothing       → text placeholder
 */

export default function VideoPreview({
  segments = [],
  currentTimeMs = 0,
  isPlaying = false,
  onTimeUpdate,
  onPlayPause,
  onSeek,
  aspectRatio = "16:9",
  previewUrl = "",
  previewStatus = "",
  previewLoading = false
}) {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const [error, setError] = useState("");
  const previewEnabled = !!previewUrl && previewStatus === "completed";
  const previewBusy = previewLoading || previewStatus === "generating";

  // ── Derived values ──
  const totalDurationMs = segments.reduce((sum, seg) => sum + (seg.duration || 0) * 1000, 0);

  // Build cumulative start times once when segments change
  const segmentStarts = useMemo(() => {
    const starts = [];
    let cum = 0;
    for (const seg of segments) {
      starts.push(cum);
      cum += (seg.duration || 0) * 1000;
    }
    return starts;
  }, [segments]);

  // Current segment index
  const currentSegmentIndex = useMemo(() => {
    for (let i = segments.length - 1; i >= 0; i--) {
      if (currentTimeMs >= segmentStarts[i]) return i;
    }
    return 0;
  }, [currentTimeMs, segmentStarts, segments.length]);

  const currentSegment = segments[currentSegmentIndex];
  const hasVideo = currentSegment?.video?.status === "completed" && currentSegment?.video?.url;

  const segmentDurationMs = (currentSegment?.duration || 0) * 1000;
  const localTimeMs = currentTimeMs - (segmentStarts[currentSegmentIndex] || 0);
  const localTimeSec = localTimeMs / 1000;

  // Progress 0→1 within current segment (used for crossfade)
  const progress = segmentDurationMs > 0 ? Math.min(localTimeMs / segmentDurationMs, 1) : 0;

  // ── Preview video sync (single file) ──
  useEffect(() => {
    if (!previewEnabled || !previewRef.current) return;
    const video = previewRef.current;

    if (video.src !== previewUrl) {
      video.src = previewUrl;
      video.load();
    }

    const targetTime = currentTimeMs / 1000;
    if (Math.abs(video.currentTime - targetTime) > 0.2) {
      video.currentTime = Math.max(0, targetTime);
    }

    if (isPlaying) {
      video.play().catch(() => setError(t("preview.playError")));
    } else {
      video.pause();
    }
  }, [previewEnabled, previewUrl, currentTimeMs, isPlaying]);

  useEffect(() => {
    if (!previewEnabled || !previewRef.current) return;
    const video = previewRef.current;
    const handleTime = () => {
      if (onTimeUpdate) onTimeUpdate(video.currentTime * 1000);
    };
    const handleEnded = () => {
      if (onPlayPause) onPlayPause();
      if (onSeek) onSeek(0);
    };
    video.addEventListener("timeupdate", handleTime);
    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("timeupdate", handleTime);
      video.removeEventListener("ended", handleEnded);
    };
  }, [onPlayPause, onSeek, onTimeUpdate, previewEnabled]);

  // ── Video sync (segment playback) ──
  useEffect(() => {
    if (!videoRef.current || !hasVideo || previewEnabled) return;
    const video = videoRef.current;

    if (video.src !== currentSegment.video.url) {
      video.src = currentSegment.video.url;
      video.load();
    }

    if (Math.abs(video.currentTime - localTimeSec) > 0.15) {
      video.currentTime = Math.max(0, localTimeSec);
    }

    if (isPlaying) {
      video.play().catch(() => setError(t("preview.playError")));
    } else {
      video.pause();
    }
  }, [hasVideo, currentSegment, localTimeSec, isPlaying, previewEnabled]);

  // ── Image URLs for slideshow ──
  const startImgUrl = currentSegment?.start_image?.url;
  const endImgUrl = currentSegment?.end_image?.url;
  const hasImages = !!(startImgUrl || endImgUrl);

  // Prompt text helper
  const promptText = typeof currentSegment?.prompt === "string"
    ? currentSegment.prompt
    : currentSegment?.prompt?.text || "";

  return (
    <div className="video-preview-container">
      <div className="video-preview-display" style={{ aspectRatio }}>
        {/* ── Mode 1: Real video ── */}
        {previewEnabled && (
          <video
            ref={previewRef}
            className="video-preview-player"
            playsInline
            onError={() => setError(t("preview.loadError"))}
          />
        )}

        {!previewEnabled && hasVideo && (
          <video
            ref={videoRef}
            className="video-preview-player"
            playsInline
            onError={() => setError(t("preview.loadError"))}
          />
        )}

        {/* ── Mode 2: Image crossfade slideshow ── */}
        {!previewEnabled && !hasVideo && hasImages && (
          <div className="video-preview-slideshow">
            {startImgUrl && (
              <img
                src={startImgUrl}
                alt=""
                className="video-preview-slide"
                style={{ opacity: 1 - progress * 0.7 }}
                draggable={false}
              />
            )}
            {endImgUrl && (
              <img
                src={endImgUrl}
                alt=""
                className="video-preview-slide"
                style={{ opacity: progress }}
                draggable={false}
              />
            )}
            {/* Progress bar at bottom */}
            <div className="video-preview-slide-progress">
              <div
                className="video-preview-slide-progress-bar"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* ── Mode 3: Text placeholder ── */}
        {!previewEnabled && !hasVideo && !hasImages && (
          <div className="video-preview-placeholder">
            {currentSegment ? (
              <>
                <span className="video-preview-placeholder-num">
                  {currentSegmentIndex + 1}
                </span>
                <span className="video-preview-placeholder-text">
                  {promptText.substring(0, 80) || t("preview.noContent")}
                </span>
                <span className="video-preview-placeholder-hint">
                  {t("preview.awaitingImages")}
                </span>
              </>
            ) : (
              <span className="video-preview-placeholder-hint">
                {t("preview.noSegment")}
              </span>
            )}
          </div>
        )}

        {/* ── Overlay: time + segment info ── */}
        <div className="video-preview-overlay">
          <div className="video-preview-segment-info">
            {currentSegmentIndex + 1}/{segments.length}
          </div>

          <div className="video-preview-time">
            {(currentTimeMs / 1000).toFixed(1)}s / {(totalDurationMs / 1000).toFixed(1)}s
          </div>

          {error && (
            <div className="video-preview-error">{error}</div>
          )}
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="video-preview-controls">
        <button
          type="button"
          className="video-preview-btn video-preview-btn-skip"
          onClick={() => onSeek?.(0)}
          title={t("preview.restart")}
        >
          <SkipBack size={20} />
        </button>

        <button
          type="button"
          className="video-preview-btn video-preview-btn-skip"
          onClick={() => onSeek?.(Math.max(0, currentTimeMs - 5000))}
          title="-5s"
        >
          <SkipBack size={16} />
        </button>

        <button
          type="button"
          className="video-preview-btn video-preview-btn-play"
          onClick={() => onPlayPause?.()}
          disabled={previewBusy}
        >
          {previewBusy ? (
            <span className="video-preview-spinner" />
          ) : isPlaying ? (
            <Pause size={28} fill="currentColor" />
          ) : (
            <Play size={28} fill="currentColor" />
          )}
        </button>

        <button
          type="button"
          className="video-preview-btn video-preview-btn-skip"
          onClick={() => onSeek?.(Math.min(totalDurationMs, currentTimeMs + 5000))}
          title="+5s"
        >
          <SkipForward size={16} />
        </button>

        <button
          type="button"
          className="video-preview-btn video-preview-btn-skip"
          onClick={() => onSeek?.(totalDurationMs)}
          title={t("preview.end")}
        >
          <SkipForward size={20} />
        </button>
      </div>
    </div>
  );
}
